/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useMemo, useState } from "react";
import {
  CubeIcon,
  MagnifyingGlassIcon,
  ServerIcon,
  ServerStackIcon,
  WifiIcon,
} from "@heroicons/react/24/outline";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Overview } from "@/api/overview";
import { MonitorAgent } from "@/api/monitor";
import { SNMPDevice } from "@/api/snmp";
import { DockerHost } from "@/api/docker";
import { RouterStatus } from "@/api/routers";
import { BrandMark } from "@/components/common/BrandMark";
import { requestFocus } from "@/utils/sidebarFocus";
import { cn } from "@/lib/utils";

type Status = "up" | "warn" | "down";

interface Device {
  id: string;
  name: string;
  status: Status;
  reason?: string;
}

interface Lane {
  key: string;
  label: string;
  tab: string;
  scope: string;
  icon: React.ComponentType<{ className?: string }>;
  devices: Device[];
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  overview?: Overview;
  agents: MonitorAgent[];
  switches: SNMPDevice[];
  dockerHosts: DockerHost[];
  routers: RouterStatus[];
  onTabChange: (tabId: string) => void;
}

const DOT: Record<Status, string> = {
  up: "bg-emerald-500",
  warn: "bg-amber-500",
  down: "bg-red-500",
};

const CHIP: Record<Status, string> = {
  up: "border-gray-200 bg-white hover:border-emerald-500/60 dark:border-white/10 dark:bg-white/[0.03] dark:hover:border-emerald-500/50",
  warn: "border-amber-500/40 bg-amber-500/[0.07] hover:border-amber-500",
  down: "border-red-500/40 bg-red-500/[0.07] hover:border-red-500",
};

const Chip: React.FC<{ device: Device; index: number; onClick: () => void }> = ({ device, index, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    title={device.reason ? `${device.name} — ${device.reason}` : device.name}
    style={{ "--i": index } as React.CSSProperties}
    className={cn(
      "nm-chip group flex min-w-0 items-center gap-2.5 rounded-lg border px-3 py-2 text-left transition-colors",
      CHIP[device.status],
    )}
  >
    <span className="relative flex h-2.5 w-2.5 flex-none">
      {device.status === "down" && (
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500/60" />
      )}
      <span className={cn("relative inline-flex h-2.5 w-2.5 rounded-full", DOT[device.status])} />
    </span>
    <span className="min-w-0 flex-1">
      <span className="block truncate text-[13px] font-medium text-gray-900 dark:text-gray-100">
        {device.name}
      </span>
      {device.reason && (
        <span
          className={cn(
            "block truncate text-[11px]",
            device.status === "down" ? "text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400",
          )}
        >
          {device.reason}
        </span>
      )}
    </span>
  </button>
);

export const NetworkMapModal: React.FC<Props> = ({
  open,
  onOpenChange,
  overview,
  agents,
  switches,
  dockerHosts,
  routers,
  onTabChange,
}) => {
  const [query, setQuery] = useState("");
  const [problemsOnly, setProblemsOnly] = useState(false);

  const lanes = useMemo<Lane[]>(() => {
    const bad = new Map<string, { sev: "crit" | "warn"; reason: string }>();
    for (const p of overview?.problems ?? []) bad.set(p.name, { sev: p.severity, reason: p.reason });
    const device = (id: string, name: string): Device => {
      const b = bad.get(name);
      return {
        id,
        name,
        status: !b ? "up" : b.sev === "crit" ? "down" : "warn",
        reason: b?.reason,
      };
    };
    const lane = (
      key: string,
      label: string,
      tab: string,
      icon: Lane["icon"],
      items: { id: string; name: string }[],
    ): Lane => ({
      key,
      label,
      tab,
      scope: tab,
      icon,
      devices: items.map((i) => device(i.id, i.name)),
    });
    return [
      lane("routers", "Routers", "routers", WifiIcon, routers.map((r) => ({ id: r.id, name: r.name }))),
      lane("switches", "Switches", "switches", ServerStackIcon, switches.map((d) => ({ id: String(d.id), name: d.name }))),
      lane("monitor", "Servers", "monitor", ServerIcon, agents.map((a) => ({ id: String(a.id), name: a.name }))),
      lane("docker", "Docker hosts", "docker", CubeIcon, dockerHosts.map((h) => ({ id: String(h.id), name: h.name }))),
    ].filter((l) => l.devices.length > 0);
  }, [overview, agents, switches, dockerHosts, routers]);

  const totals = useMemo(() => {
    const t = { up: 0, warn: 0, down: 0 };
    for (const l of lanes) for (const d of l.devices) t[d.status]++;
    return t;
  }, [lanes]);

  const q = query.trim().toLowerCase();
  const visible = lanes
    .map((l) => ({
      ...l,
      shown: l.devices
        .filter((d) => (!q || d.name.toLowerCase().includes(q)) && (!problemsOnly || d.status !== "up"))
        // worst first, then alphabetical
        .sort((a, b) => {
          const rank = { down: 0, warn: 1, up: 2 } as const;
          return rank[a.status] - rank[b.status] || a.name.localeCompare(b.name);
        }),
    }))
    .filter((l) => l.shown.length > 0);

  const jump = (lane: Lane, id: string) => {
    onTabChange(lane.tab);
    requestFocus(lane.scope, id);
    onOpenChange(false);
  };

  const total = totals.up + totals.warn + totals.down;
  const overall: Status = totals.down > 0 ? "down" : totals.warn > 0 ? "warn" : "up";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[min(760px,90vh)] w-[calc(100%-2rem)] flex-col gap-4 sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>Network map</DialogTitle>
        </DialogHeader>

        {/* summary + controls */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-4 text-[12px] text-gray-600 dark:text-gray-300">
            <span className="flex items-center gap-1.5">
              <span className={cn("h-2 w-2 rounded-full", DOT.up)} />
              {totals.up} healthy
            </span>
            <span className="flex items-center gap-1.5">
              <span className={cn("h-2 w-2 rounded-full", DOT.warn)} />
              {totals.warn} warning
            </span>
            <span className="flex items-center gap-1.5">
              <span className={cn("h-2 w-2 rounded-full", DOT.down)} />
              {totals.down} down
            </span>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <label className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Find a device…"
                className="h-8 w-44 rounded-lg border border-gray-200 bg-white pl-8 pr-2 text-[12.5px] text-gray-900 outline-none focus:border-blue-500 dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-100"
              />
            </label>
            <button
              type="button"
              onClick={() => setProblemsOnly((v) => !v)}
              aria-pressed={problemsOnly}
              className={cn(
                "h-8 rounded-lg border px-3 text-[12.5px] transition-colors",
                problemsOnly
                  ? "border-red-500/50 bg-red-500/10 text-red-600 dark:text-red-400"
                  : "border-gray-200 text-gray-600 hover:bg-gray-100 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5",
              )}
            >
              Problems only
            </button>
          </div>
        </div>

        {/* topology: hub -> lanes */}
        <div className="modal-scrollbar min-h-0 flex-1 overflow-y-auto pr-2">
          <div className="flex flex-col gap-4 md:flex-row md:gap-0">
            {/* hub */}
            <div className="flex flex-none items-start md:w-44 md:pr-6">
              <div className={cn("nm-hub flex w-full items-center gap-3 rounded-lg border border-gray-200 bg-white dark:border-white/10 dark:bg-white/[0.03] px-4 py-3 md:flex-col md:items-start md:gap-2", overall === "down" && "nm-hub--down", overall === "warn" && "nm-hub--warn")}>
                <BrandMark size={40} />
                <div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Nyrvex</div>
                  <div className="flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                    <span className={cn("h-1.5 w-1.5 rounded-full", DOT[overall])} />
                    {total} devices
                  </div>
                </div>
              </div>
            </div>

            {/* lanes, joined to the hub by a trunk line */}
            <div className="relative flex-1 space-y-5 md:pl-6">
              {/* the trunk draws itself from the hub down, and a pulse runs along it */}
              <span className="nm-trunk absolute bottom-0 left-0 top-0 hidden w-px bg-gray-200 md:block dark:bg-white/10" aria-hidden="true" />
              <span className="nm-flow absolute left-[-1px] top-0 hidden h-8 w-[3px] rounded-full bg-emerald-500/70 md:block" aria-hidden="true" />
              {visible.length === 0 ? (
                <p className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                  {problemsOnly && !q ? "No problems — everything is healthy." : "No devices match."}
                </p>
              ) : (
                visible.map((lane, laneIdx) => {
                  const Icon = lane.icon;
                  const up = lane.devices.filter((d) => d.status === "up").length;
                  const laneStatus: Status = lane.devices.some((d) => d.status === "down")
                    ? "down"
                    : lane.devices.some((d) => d.status === "warn")
                      ? "warn"
                      : "up";
                  return (
                    <section key={lane.key} className="nm-lane relative" style={{ "--i": laneIdx } as React.CSSProperties}>
                      {/* branch from the trunk */}
                      <span className="nm-branch absolute -left-6 top-4 hidden h-px w-6 bg-gray-200 md:block dark:bg-white/10" />
                      <span
                        className={cn(
                          "nm-node absolute -left-[27px] top-[13px] hidden h-[7px] w-[7px] rounded-full md:block",
                          DOT[laneStatus],
                        )}
                      />
                      <div className="mb-2 flex items-center gap-2.5">
                        <Icon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                        <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-100">{lane.label}</h3>
                        <span className="text-[12px] tabular-nums text-gray-500 dark:text-gray-400">
                          {up}/{lane.devices.length}
                        </span>
                        <span className="flex h-1 w-24 overflow-hidden rounded-full bg-gray-200 dark:bg-white/10">
                          {(["up", "warn", "down"] as Status[]).map((st) => {
                            const n = lane.devices.filter((d) => d.status === st).length;
                            return n ? (
                              <span
                                key={st}
                                className={cn("nm-fill h-full", DOT[st])}
                                style={{ width: `${(n / lane.devices.length) * 100}%` }}
                              />
                            ) : null;
                          })}
                        </span>
                        {lane.shown.length !== lane.devices.length && (
                          <span className="text-[11px] text-gray-400">showing {lane.shown.length}</span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        {lane.shown.map((d, i) => (
                          <Chip key={d.id} device={d} index={i} onClick={() => jump(lane, d.id)} />
                        ))}
                      </div>
                    </section>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <div className="text-[11px] text-gray-500 dark:text-gray-400">Click a device to open it.</div>
      </DialogContent>
    </Dialog>
  );
};
